import { useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { Link } from 'react-router-dom'
import {
  fetchScratchRecipients,
  type ScratchRecipients,
} from '@/lib/campaign-scratch-card'
import styles from './CampaignAchievementRecipients.module.css'
import scratchStyles from './ScratchCardConfigEditor.module.css'

const INITIAL_FILTERS = { search: '', result: 'winners', status: '', from: '', to: '' }

export function ScratchCardRecipients({
  campaignId,
  token,
}: {
  campaignId: number
  token?: string
}) {
  const [expanded, setExpanded] = useState(false)
  const [filters, setFilters] = useState(INITIAL_FILTERS)
  const applied = useRef(INITIAL_FILTERS)
  const [data, setData] = useState<ScratchRecipients | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const request = useRef<AbortController | null>(null)
  useEffect(() => () => request.current?.abort(), [])

  async function load(page: number, selected = applied.current) {
    request.current?.abort()
    const controller = new AbortController()
    request.current = controller
    setBusy(true)
    setError('')
    const query = new URLSearchParams({ page: String(page) })
    Object.entries(selected).forEach(([key, value]) => {
      if (value) query.set(key, value)
    })
    try {
      const result = await fetchScratchRecipients(
        campaignId,
        query,
        token,
        controller.signal
      )
      if (!controller.signal.aborted) {
        setData(result)
        applied.current = selected
      }
    } catch {
      if (!controller.signal.aborted)
        setError('Não foi possível carregar os resultados. Tente novamente.')
    } finally {
      if (!controller.signal.aborted) setBusy(false)
    }
  }

  return (
    <div className={styles.container}>
      <button
        type="button"
        className={styles.toggle}
        aria-expanded={expanded}
        aria-controls="scratch-card-recipients"
        onClick={() => {
          setExpanded(!expanded)
          if (!expanded) void load(1)
          else {
            request.current?.abort()
            setBusy(false)
          }
        }}
      >
        <Icon icon="mdi:account-group-outline" aria-hidden />
        <span>
          {expanded
            ? 'Recolher resultados da raspadinha'
            : 'Ver participantes e ganhadores da raspadinha'}
        </span>
        <Icon icon={expanded ? 'mdi:chevron-up' : 'mdi:chevron-down'} aria-hidden />
      </button>
      {expanded && (
        <section
          id="scratch-card-recipients"
          className={styles.panel}
          aria-label="Resultados da raspadinha"
          aria-busy={busy}
        >
          <div className={styles.heading}>
            <h3>Quem já revelou uma surpresa</h3>
            {data && (
              <span className={styles.count}>{data.pagination.total} participações</span>
            )}
          </div>
          <div
            className={scratchStyles.filters}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                void load(1, filters)
              }
            }}
          >
            <label>
              Buscar participante
              <input
                type="search"
                maxLength={120}
                placeholder="Nome, e-mail ou telefone"
                value={filters.search}
                onChange={(event) =>
                  setFilters({ ...filters, search: event.target.value })
                }
              />
            </label>
            <label>
              Resultado
              <select
                value={filters.result}
                onChange={(event) =>
                  setFilters({ ...filters, result: event.target.value })
                }
              >
                <option value="winners">Todos os ganhadores</option>
                <option value="all">Todas as participações</option>
                <option value="lucky_number">Números extras</option>
                <option value="coupon">Cupons</option>
                <option value="none">Sem prêmio</option>
              </select>
            </label>
            <label>
              Situação
              <select
                value={filters.status}
                onChange={(event) =>
                  setFilters({ ...filters, status: event.target.value })
                }
              >
                <option value="">Todas</option>
                <option value="pending">Aguardando uso ou resgate</option>
                <option value="used">Utilizado ou resgatado</option>
                <option value="no_prize">Sem prêmio</option>
              </select>
            </label>
            <label>
              De
              <input
                type="date"
                value={filters.from}
                onChange={(event) => setFilters({ ...filters, from: event.target.value })}
              />
            </label>
            <label>
              Até
              <input
                type="date"
                min={filters.from || undefined}
                value={filters.to}
                onChange={(event) => setFilters({ ...filters, to: event.target.value })}
              />
            </label>
            <button type="button" disabled={busy} onClick={() => void load(1, filters)}>
              {busy ? 'Buscando…' : 'Aplicar filtros'}
            </button>
          </div>
          {error ? (
            <p role="alert" className={styles.feedback}>
              {error}
            </p>
          ) : busy ? (
            <p role="status" className={styles.feedback}>
              Carregando participações…
            </p>
          ) : data?.items.length ? (
            <div className={scratchStyles.tableWrap}>
              <table className={scratchStyles.table}>
                <thead>
                  <tr>
                    <th>Participante</th>
                    <th>Resultado</th>
                    <th>Situação</th>
                    <th>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        {item.donor ? (
                          <>
                            <Link to={`/clientes/${item.donor.id}/editar`}>
                              {item.donor.name}
                            </Link>
                            <small>
                              {item.donor.email ||
                                item.donor.phone ||
                                'Sem contato cadastrado'}
                            </small>
                          </>
                        ) : (
                          'Participante indisponível'
                        )}
                      </td>
                      <td>
                        {item.prize.prize_type === 'lucky_number'
                          ? `${item.prize.prize_payload?.quantity} números extras`
                          : item.prize.prize_type === 'coupon'
                            ? `Cupom de ${item.prize.prize_payload?.discount_percent}%`
                            : 'Sem prêmio'}
                        <small>{item.prize.prize_payload?.label}</small>
                      </td>
                      <td>
                        {item.prize.status === 'used'
                          ? 'Utilizado / resgatado'
                          : item.prize.prize_type === 'none'
                            ? 'Sem prêmio'
                            : 'Aguardando uso / resgate'}
                        <small>
                          {item.revealed_at
                            ? 'Raspadinha revelada'
                            : 'Aguardando revelação'}
                        </small>
                      </td>
                      <td>{new Date(item.created_at).toLocaleString('pt-BR')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className={styles.feedback}>
              Nenhuma participação encontrada com esses filtros.
            </p>
          )}
          {data && (
            <div className={styles.pagination}>
              <span>
                Página {data.pagination.current_page} de {data.pagination.last_page}
              </span>
              <div>
                <button
                  type="button"
                  disabled={busy || data.pagination.current_page <= 1}
                  onClick={() => void load(data.pagination.current_page - 1)}
                >
                  Anterior
                </button>
                <button
                  type="button"
                  disabled={
                    busy || data.pagination.current_page >= data.pagination.last_page
                  }
                  onClick={() => void load(data.pagination.current_page + 1)}
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  )
}
